import { useEffect } from "react";
import { create } from "zustand";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

// "kiosk" (mig 410, 30 Sep 2026) — the crew tablet: a shared, view-only login tied to a crew, no employee.
export type Role = "director" | "manager" | "supervisor" | "field" | "subcontractor" | "kiosk";
export type LoginType = "employee" | "subcontractor" | "auditor" | "client";

export interface CoretelUser {
  user_id: string;
  auth_user_id: string;
  login_email: string;
  role: Role;
  // payroll_access — orthogonal to role. Grants visibility of sensitive
  // employee data (bank/super/TFN) plus access to the payroll summary/export.
  // Set per-user in Admin > Users; directors have it by role.
  payroll_access: boolean;
  // login_type drives onboarding routing — 'subcontractor' + no
  // onboarding_completed_at means they go through the subbie wizard
  // (T-154) instead of the standard employee onboarding flow.
  login_type: LoginType;
  employee_id: string | null;
  first_name: string | null;
  last_name: string | null;
  profile_photo_url: string | null;
  onboarding_completed_at: string | null;
  // mig 410 — crew tablet only
  crew_id: string | null;
  device_label: string | null;
}

interface AuthState {
  session: Session | null;
  user: CoretelUser | null;
  loading: boolean;
  error: string | null;
  setSession: (s: Session | null) => void;
  setUser: (u: CoretelUser | null) => void;
  setError: (e: string | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user: null,
  loading: true,
  error: null,
  setSession: (session) => set({ session }),
  setUser:    (user)    => set({ user, loading: false }),
  setError:   (error)   => set({ error }),

  signIn: async (email, password) => {
    set({ loading: true, error: null });
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) set({ error: error.message, loading: false });
  },
  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, user: null });
  },
}));

// Call this once near the root so the store stays in sync with Supabase auth
export function useAuthBootstrap() {
  const { setSession, setUser, setError } = useAuthStore();

  useEffect(() => {
    async function loadUserRow(session: Session | null) {
      if (!session) {
        setUser(null);
        return;
      }
      // (supabase as any): crew_id / device_label are newer than database.types.ts (mig 410)
      const { data, error } = await (supabase as any)
        .from("users")
        .select(`
          user_id, auth_user_id, login_email, role_name, login_type, employee_id,
          payroll_access, onboarding_completed_at, is_active, crew_id, device_label,
          employee:v_employees(first_name, last_name, profile_photo_url)
        `)
        .eq("auth_user_id", session.user.id)
        .maybeSingle();

      if (error) { setError(error.message); setUser(null); return; }
      if (!data)  { setError("Your login isn't linked to an employee record. Ask a director to complete setup."); setUser(null); return; }
      // mig 410 — a crew tablet an admin has signed out (is_active = false) is
      // signed out here too, on its next load, before its token expires.
      if ((data as any).role_name === "kiosk" && (data as any).is_active === false) {
        await supabase.auth.signOut();
        setError("This tablet has been signed out by the office.");
        setUser(null);
        return;
      }

      setUser({
        user_id: data.user_id as string,
        auth_user_id: data.auth_user_id,
        login_email: data.login_email,
        role: data.role_name as Role,
        payroll_access: data.payroll_access ?? false,
        login_type: (data.login_type ?? "employee") as LoginType,
        employee_id: data.employee_id,
        first_name:  data.employee?.first_name ?? null,
        last_name:   data.employee?.last_name  ?? null,
        profile_photo_url: data.employee?.profile_photo_url ?? null,
        onboarding_completed_at: data.onboarding_completed_at,
        crew_id: (data as any).crew_id ?? null,
        device_label: (data as any).device_label ?? null,
      });
    }

    // Initial load
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      loadUserRow(data.session);
    });

    // Subscribe to changes
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      loadUserRow(session);
      // T-454 — record last sign-in (RPC; users table is manager-write only).
      if (event === "SIGNED_IN" && session) {
        supabase.schema("app").rpc("touch_last_login").then(() => {}, () => {});
      }
    });

    return () => sub.subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// Convenience for screens that require auth
export function useRequireAuth(): CoretelUser | null {
  return useAuthStore((s) => s.user);
}

// Helper — check if current user has at least a given role
export function hasRole(user: CoretelUser | null, role: Role | Role[]): boolean {
  if (!user) return false;
  const hierarchy: Record<Role, number> = {
    director: 4, manager: 3, supervisor: 2, field: 1, subcontractor: 0, kiosk: 0,
  };
  const wanted = Array.isArray(role) ? role : [role];
  return wanted.some(r => hierarchy[user.role] >= hierarchy[r]);
}
